/* ──────────────────────────────────────────────────────────────────────────
   Velvet Vanity restyle — behaviour layer.

   Loaded last, after theme.js. Keep it small and additive: anything that can
   be done in assets/vv-restyle.css belongs there instead.

   Issue #26 — selected shade name beside the option label.
   Palo Alto's swatch <legend> prints only the option name ("Color"). The
   Orichi Color Swatch app used to render the selected shade name, so removing
   the app would have dropped it from all 12 colour PDPs. The markup is emitted
   by snippets/product.liquid; this keeps it in sync with the chosen variant.

   The theme dispatches a bubbling `theme:variant:change` with detail.variant,
   whose `options` array is positional — so [data-option-position] on our span
   indexes straight into it.
   ────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  function sync(container, variant) {
    if (!variant || !variant.options) return;
    container.querySelectorAll('[data-vv-swatch-value]').forEach(function (el) {
      var pos = parseInt(el.getAttribute('data-option-position'), 10);
      var value = variant.options[pos - 1];
      if (value) el.textContent = value;
    });
  }

  /* #60: the CTA reads "Add to Bag — RM69". Liquid renders that on first paint,
     but the theme's own product JS rewrites the button from theme.strings on
     init and on every variant change, which put "Add to Cart" straight back.
     Rather than renaming the shared `add_to_cart` string — the cart drawer and
     quick view use it too — the label is re-applied here, after the theme has
     had its turn.

     Availability is left to the theme: when a variant is sold out it writes its
     own label, and overwriting that would hide the sold-out state. */
  function syncCtaLabel(scope, variant) {
    var btn = scope.querySelector('.product__submit__add');
    if (!btn || !variant || !variant.available) return;
    var label = btn.querySelector('[data-add-to-cart-text]');
    if (!label) return;

    /* The label comes off the button itself rather than a global, so it stays
       translatable through the normal locale file. */
    var text = btn.getAttribute('data-vv-add-to-bag') || 'Add to Bag';
    if (variant.price > 0) {
      text += ' \u2014 ' + vvMoney(variant.price);
    }
    label.textContent = text;
  }

  /* Shopify.formatMoney is NOT global here — it is bundled inside theme.js, the
     same way Flickity is. window.theme.moneyFormat IS exposed, so the price is
     formatted from that template directly.

     The format string carries markup ("<span class=money>RM{{amount}} MYR</span>")
     and this value is written with textContent, so the tags are stripped — the
     same trap that printed a literal "<span cl…" in the #16 cross-sell rows. */
  function vvMoney(cents) {
    var fmt = (window.theme && window.theme.moneyFormat) || '{{amount}}';

    function commas(n) {
      return String(n).replace(/(\d)(?=(\d{3})+$)/g, '$1,');
    }

    var whole = Math.floor(Math.abs(cents) / 100);
    var decimals = (Math.abs(cents) % 100).toString().padStart(2, '0');
    var rounded = Math.round(Math.abs(cents) / 100);

    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, commas(whole) + '.' + decimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, commas(whole) + ',' + decimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, commas(rounded))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, commas(rounded))
      .replace(/<[^>]*>/g, '')
      .trim();
  }

  document.addEventListener('theme:variant:change', function (event) {
    var container = event.target.closest ? event.target.closest('[data-section-id]') : null;
    var scope = container || document;
    var variant = event.detail && event.detail.variant;
    sync(scope, variant);
    syncSoldOut(scope, variant);
    syncCtaLabel(scope, variant);
  });

  /* #15: the theme swaps the add-to-cart LABEL on variant change but never sets
     the `disabled` property, so a sold-out shade left a submittable button that
     merely looked disabled. Liquid handles first render; this handles the
     shopper switching shade. */
  function syncSoldOut(container, variant) {
    if (!variant) return;
    container.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
      btn.disabled = !variant.available;
    });
  }

  /* Clicking a swatch for an option the variant doesn't change (e.g. a second
     option on a multi-option product) still fires the event, but a direct
     input change is the cheapest path for the common single-option case. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.classList || !input.classList.contains('swatch__input')) return;
    var fieldset = input.closest('[data-swatches-container]');
    if (!fieldset) return;
    var label = fieldset.querySelector('[data-vv-swatch-value]');
    if (label) label.textContent = input.value;

    /* #60: swap the one-line shade description. It is per VARIANT, so it is
       carried on each swatch input rather than looked up from the product JSON,
       which does not include variant metafields. Hidden entirely when the shade
       has no description, so a half-filled catalogue shows a gap rather than a
       stale line from the previous shade. */
    var form = fieldset.closest('form') || document;
    var note = form.querySelector('[data-vv-shade-description]');
    if (!note) note = document.querySelector('[data-vv-shade-description]');
    if (note) {
      var desc = input.getAttribute('data-shade-description') || '';
      note.textContent = desc;
      note.hidden = desc === '';
    }
  });

  /* #14: keep the gallery counter ("1 / 7") in step with the carousel.

     Flickity is bundled inside theme.js and is NOT exposed as window.Flickity,
     so there is no instance to attach a 'change' listener to. What IS public is
     the DOM: Flickity puts `is-selected` on the active cell. Observing that is
     both dependency-free and automatically correct for every way the slide can
     change -- arrow, thumbnail, drag, swipe, keyboard, or a variant switch
     jumping to that variant's featured image. */
  function initGalleryCounter(gallery) {
    var counter = gallery.querySelector('[data-vv-gallery-counter]');
    var slider = gallery.querySelector('[data-product-single-media-slider]');
    if (!counter || !slider) return;

    var current = counter.querySelector('[data-vv-gallery-current]');
    if (!current) return;

    function cells() {
      return slider.querySelectorAll('[data-product-single-media-wrapper]');
    }

    var total = counter.querySelector('[data-vv-gallery-total]');

    /* #74: count only what the shopper can reach. With per-variant groups the gallery
       is confined to one shade's media, so "3 / 25" would be meaningless. The out-of-
       group cells stay in the layout (see initVariantGallery for why), so this keys off
       the marker class rather than visibility. Products without groups carry no marker
       and get the full count, exactly as before. */
    function visibleCells() {
      return Array.prototype.filter.call(cells(), function (cell) {
        return !cell.classList.contains('vv-media--out-of-group');
      });
    }

    function sync() {
      var list = visibleCells();
      if (total) total.textContent = list.length;
      for (var i = 0; i < list.length; i++) {
        if (list[i].classList.contains('is-selected')) {
          current.textContent = i + 1;
          return;
        }
      }
    }

    /* subtree:true because Flickity moves the cells into its own .flickity-slider
       wrapper on init, so the cells are not direct children of `slider`. */
    new MutationObserver(sync).observe(slider, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    sync();
  }

  /* #74: per-variant gallery.

     Shows only the selected shade's media. The grouping is computed in Liquid and
     handed over as a media-id -> variant-id map on the gallery (see
     snippets/product.liquid); this only applies it.

     Why hide rather than remove: Flickity is bundled inside theme.js and exposes no
     instance -- not on window, not as an expando, not via window.theme -- so there is
     no API to add or remove cells. Hiding and firing a resize is the one lever it
     does respond to: hidden cells measure 0 and it repositions around them.

     Re-rendering the section instead was tried and does not work -- dispatching
     `shopify:section:load`, on the gallery or on the section, does not re-initialise
     Flickity. See #73 for the full write-up. */
  function initVariantGallery(gallery) {
    var raw = gallery.getAttribute('data-vv-media-groups');
    if (!raw) return;

    var groups;
    try {
      groups = JSON.parse(raw);
    } catch (e) {
      return;
    }

    var slider = gallery.querySelector('[data-product-single-media-slider]');
    if (!slider) return;

    var thumbsWrap = gallery.querySelector('[data-product-single-media-thumbs]');
    var activeGroup = null;
    var lastIndex = 0;

    function cells() {
      return Array.prototype.slice.call(gallery.querySelectorAll('[data-product-single-media-wrapper]'));
    }

    function thumbs() {
      return thumbsWrap
        ? Array.prototype.slice.call(thumbsWrap.querySelectorAll('[data-thumbnail-id]'))
        : [];
    }

    function inGroup(el) {
      if (!activeGroup) return true;
      var group = groups[el.getAttribute('data-id')];
      return group === activeGroup || group === 'shared';
    }

    /* "In the group" is now a class, not visibility — the cells stay in the layout. */
    function groupCells() {
      return cells().filter(function (cell) {
        return !cell.classList.contains('vv-media--out-of-group');
      });
    }

    function filter(variantId) {
      /* Only constrain a carousel. The two #54 bespoke templates render the gallery as a
         grid with no Flickity, where every image is on screen at once — marking cells
         there does nothing visible and would be a lie waiting to happen if that template
         ever gained a slider. `peptide-glo-lip-oil` is the live example: it passes the
         grouping gate, and without this its counter read "1 / 1" over 14 visible images.
         (Harmless today only because that template hides the counter.) */
      if (!slider.classList.contains('flickity-enabled')) return;

      var key = variantId == null ? null : String(variantId);
      var owns = key !== null && Object.keys(groups).some(function (id) {
        return groups[id] === key;
      });

      /* Agreed fallback: a variant with no media of its own shows the WHOLE gallery
         rather than an empty frame, so a half-assigned product degrades to today's
         behaviour instead of looking broken. */
      activeGroup = owns ? key : null;

      /* The main slider's cells are MARKED, not hidden. Hiding them is what we tried
         first and it cannot be made to work: removing cells from layout makes Flickity
         recalculate, and on roughly every other variant change it settled on
         `.flickity-viewport { height: 0 }` and the gallery collapsed to a sliver.
         Resizing afterwards does not recover it — Flickity early-returns when the width
         is unchanged — and forcing a real width change did not help either. Selecting a
         visible cell first does not help: the collapse happens with a visible cell
         selected, so it is not adaptive height either.

         So the slider keeps every cell at full size and can never collapse. What the
         shopper can REACH is constrained instead, by the arrow clamp and the snap-back
         below. The thumbnails are genuinely hidden — that strip measured correctly
         throughout, because it is not the element whose height the gallery depends on. */
      cells().forEach(function (cell) {
        cell.classList.toggle('vv-media--out-of-group', !inGroup(cell));
      });
      thumbs().forEach(function (thumb) {
        thumb.style.display = inGroup(thumb) ? '' : 'none';
      });

      reflow();
    }

    /* Both the slider AND the thumbnail strip are separate Flickity instances, and each
       sizes itself from whatever is measurable at the instant it recalculates. Resizing
       once right after hiding cells measures too early — Flickity wrote
       `.flickity-viewport { height: 0 }` and the gallery collapsed to a sliver while
       every DOM assertion still passed. On a variant change it also races the theme's own
       `updateProductImage`.

       So rather than guess a delay, check the result and retry: the viewport having zero
       height while a visible cell has real height is the signature of a bad measurement.
       Stops as soon as it is right, and gives up after ~1s so it can never spin. */
    function reflow() {
      /* Firing `resize` alone is not enough, and the reason is worth recording: Flickity
         early-returns from its resize handler when the element's width is unchanged. On
         a variant change the width never changes, so the event was a complete no-op —
         instrumenting the viewport showed its height set to 0 once and then never
         touched again, however many times we re-fired.

         So force a width it has not seen: shrink by a pixel, let it recalculate at that
         size, then restore and let it recalculate again. Two genuine passes, no guessing
         at delays. Both Flickity instances (slider and thumbnails) key off the window
         event, so one nudge drives both. */
      function nudge(el) {
        if (!el) return;
        var original = el.style.width;
        el.style.width = (el.getBoundingClientRect().width - 1) + 'px';
        window.dispatchEvent(new Event('resize'));
        el.style.width = original;
        window.dispatchEvent(new Event('resize'));
      }

      requestAnimationFrame(function () {
        nudge(thumbsWrap);
      });
    }

    /* The theme already moves the carousel to the variant's featured media on change,
       and that media is by definition the first of its group -- so filtering after it
       lands on the right slide without us selecting anything. */
    document.addEventListener('theme:variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      filter(variant ? variant.id : null);
    });

    /* Stop at the group boundary rather than wrapping. Flickity still holds every cell,
       so `next` walks into the hidden ones -- and because they are zero-width the arrow
       looks broken while the selection drifts into another shade. Capture phase, so this
       runs before Flickity's own handler on the button. */
    gallery.addEventListener('click', function (event) {
      if (!activeGroup) return;
      var btn = event.target.closest && event.target.closest('.flickity-prev-next-button');
      if (!btn) return;

      var list = groupCells();
      var index = list.findIndex(function (c) { return c.classList.contains('is-selected'); });
      if (index === -1) return;

      var atEnd = btn.classList.contains('next') && index === list.length - 1;
      var atStart = btn.classList.contains('previous') && index === 0;
      if (atEnd || atStart) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    /* Drag, swipe and keyboard bypass the arrows entirely, so the boundary is enforced a
       second time here: if the selection settles outside the group, walk it back in.

       It walks with the ARROW BUTTONS, one step per observation. Clicking the matching
       thumbnail would be the obvious move and was the first attempt — but thumbnail
       clicks do not drive selection programmatically, neither on the tile nor on the
       anchor inside it, so the snap-back silently never happened and a drag could leave
       the shopper on another shade's image with the counter still reading "4 / 4".
       The arrows are the one selection lever that demonstrably works.

       One step at a time is deliberate: each click re-fires this observer, so it
       converges without needing to compute a distance, and it cannot loop once the
       selection is back inside the group. */
    new MutationObserver(function () {
      if (!activeGroup) return;
      var all = cells();
      var selected = all.filter(function (c) { return c.classList.contains('is-selected'); })[0];
      if (!selected) return;

      var list = groupCells();
      var index = list.indexOf(selected);
      if (index !== -1) {
        lastIndex = index;
        return;
      }

      /* Which side did it fall off? Compare DOM positions -- the groups are contiguous
         runs of product.media, so "after the last of the group" means it went forward. */
      var here = all.indexOf(selected);
      var lastOfGroup = all.indexOf(list[list.length - 1]);
      var backwards = here > lastOfGroup;

      var btn = gallery.querySelector(
        backwards ? '.flickity-prev-next-button.previous' : '.flickity-prev-next-button.next'
      );
      if (btn) btn.click();
    }).observe(gallery, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    /* First render. Rather than hunting for the current variant id -- which lives in
       different places depending on whether the page was opened plain, deep-linked with
       ?variant=, or restored from history -- read it back out of the gallery itself:
       Shopify renders the FEATURED media from the current variant, and that media is by
       definition the first of its own group. So whichever group owns the opening slide
       is the group to show. */
    function applyOpeningGroup() {
      var opening = cells().filter(function (c) { return c.classList.contains('is-selected'); })[0] || cells()[0];
      if (!opening) return;
      var openingGroup = groups[opening.getAttribute('data-id')];
      if (openingGroup && openingGroup !== 'shared') filter(openingGroup);
    }

    /* Wait for Flickity before filtering anything. It initialises after this script runs,
       and filtering first meant it measured a gallery whose cells were already hidden —
       which is how the mobile gallery ended up 0px tall. Polled rather than evented
       because the bundle exposes no ready signal; capped so a layout that never gets a
       carousel (the stacked gallery, quick view) simply never applies. */
    var waited = 0;
    (function whenReady() {
      var ready = slider.classList.contains('flickity-enabled') &&
                  (!thumbsWrap || thumbsWrap.classList.contains('flickity-enabled'));
      if (ready) return applyOpeningGroup();
      if ((waited += 100) > 4000) return applyOpeningGroup();
      setTimeout(whenReady, 100);
    })();
  }

  function initGalleryCounters() {
    document.querySelectorAll('[data-product-single-media-group]').forEach(function (gallery) {
      initGalleryCounter(gallery);
      initVariantGallery(gallery);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGalleryCounters);
  } else {
    initGalleryCounters();
  }

  /* #16: mirror the price when a cross-sell shade is picked.

     Correctness does NOT depend on this. The picker is a <select name="id">
     inside the product form, so Shopify adds whichever variant is selected even
     with JavaScript disabled. This only keeps the displayed price honest when
     shades are priced differently. */
  /* Announcement bar: rotate the messages on mobile.

     The inline layout (ours, from #4) puts every message on one line. That is
     right on desktop and wrong on a phone, where three messages wrapped onto
     three lines and made the bar taller than the header.

     Why not reuse the theme's slider layout: AnnouncementBar.initSlider() runs
     Flickity at every width and re-inits it on resize, so choosing 'slider'
     would hand the DESKTOP bar to a carousel too. Rotating the inline markup
     ourselves keeps each layout doing what it is good at.

     Progressive enhancement: the --rotating class is only added when the
     viewport is narrow AND reduced motion is not requested. Otherwise the CSS
     fallback leaves every message visible and static. */
  /* #60: mobile sticky add-to-bag bar.

     The theme has no product sticky bar — its only sticky mechanisms are the
     collection/search filter bar and the sticky header, and `product_sticky_enable`
     is the DESKTOP sticky buy column. So this is built rather than configured.

     It deliberately does NOT contain its own form. A second form would be a second
     source of truth for the selected variant, the disabled state and the price,
     and the two would drift the moment either changed. Instead the bar's button
     forwards its click to the real add-to-cart button and mirrors its label and
     disabled state, so the page keeps one buy control.

     Shown once the real button has scrolled out of view, which is what makes it
     useful and also means it never covers the control it stands in for. */
  /* #44: desktop filter toggle.

     Deliberately NOT wired to the theme's [data-toggle-filters]: that button runs
     showFiltersDrawer(), which traps focus inside the panel. Correct for a mobile
     sheet, wrong for an inline desktop column — it would stop the shopper tabbing
     out to the grid. The theme's button and its mobile drawer are untouched.

     State lives in a class on .collection__inner; the grid reflows on its own
     because the sidebar is a float. */
  function initFilterToggle() {
    var btn = document.querySelector('[data-vv-filter-toggle]');
    /* Guard BEFORE dereferencing. Only the collection and search templates render
       this button, so `btn` is null on every other page — and because this script
       is deferred, `document.readyState` is never 'loading' and each init below
       runs synchronously inside one IIFE. A throw here therefore took out every
       feature declared after it: the sticky add-to-bag bar, the announcement
       rotator and the cross-sell price swap. See #64. */
    if (!btn) return;

    var inner = btn.closest('.vv-filters-collapsible') || document.querySelector('.vv-filters-collapsible');
    if (!inner) return;

    var label = btn.querySelector('[data-vv-filter-toggle-text]');
    var shown = btn.getAttribute('data-label-hide') || 'Hide Filters';

    /* Both labels come off the button so they stay translatable. */
    var hidden = label ? label.textContent.trim() : 'Show Filters';

    btn.addEventListener('click', function () {
      var open = inner.classList.toggle('collection__inner--vv-filters-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (label) label.textContent = open ? shown : hidden;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFilterToggle);
  } else {
    initFilterToggle();
  }

  function initStickyAtc() {
    var form = document.querySelector('[data-product-form]');
    var realBtn = document.querySelector('[data-add-to-cart]');
    if (!form || !realBtn) return;
    if (document.querySelector('.vv-sticky-atc')) return;

    var product = document.querySelector('[data-section-type="product-template"]') || document;
    var titleEl = product.querySelector('.product__title');
    var imgEl = product.querySelector('.product-single__media--image img, .product-single__media img');
    if (!titleEl) return;

    var bar = document.createElement('div');
    bar.className = 'vv-sticky-atc';

    if (imgEl && imgEl.currentSrc) {
      var thumb = document.createElement('img');
      thumb.className = 'vv-sticky-atc__media';
      thumb.src = imgEl.currentSrc;
      thumb.alt = '';
      bar.appendChild(thumb);
    }

    var text = document.createElement('div');
    text.className = 'vv-sticky-atc__text';
    var title = document.createElement('p');
    title.className = 'vv-sticky-atc__title';
    title.textContent = titleEl.textContent.trim();
    var shade = document.createElement('p');
    shade.className = 'vv-sticky-atc__shade';
    text.appendChild(title);
    text.appendChild(shade);
    bar.appendChild(text);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn--primary btn--solid vv-sticky-atc__btn';
    bar.appendChild(btn);

    /* Mirror label + disabled state from the real button, so sold-out shades and
       the price stay correct without this bar knowing anything about variants. */
    function mirror() {
      var label = realBtn.querySelector('[data-add-to-cart-text]');
      var text = label ? label.textContent.trim() : btn.textContent;

      /* FULL label, price included (#116). This used to strip everything after
         the em dash, because the button only had ~150px of text width once the
         Smile.io launcher had claimed the bottom-left corner, and
         "Add to Bag — RM69.00 MYR" wrapped to two lines and made the bar 83px
         tall. Smile was removed in #99 and the button is full width now, so the
         constraint is gone and the price is back.

         Mirroring the real button verbatim also keeps priceless states — "Sold
         Out", "Unavailable" — correct without this bar knowing about variants. */
      btn.textContent = text;
      btn.disabled = realBtn.disabled;
      var selected = document.querySelector('[data-vv-swatch-value]');
      shade.textContent = selected ? selected.textContent.trim() : '';
      shade.hidden = !shade.textContent;
    }

    btn.addEventListener('click', function () {
      if (realBtn.disabled) return;
      realBtn.click();
    });

    document.body.appendChild(bar);
    mirror();

    new MutationObserver(mirror).observe(realBtn, {
      subtree: true, childList: true, characterData: true,
      attributes: true, attributeFilter: ['disabled']
    });

    /* The launcher-lift and the drawer rule both need to know the bar's real
       height, and it is not a constant: it depends on env(safe-area-inset-bottom)
       and on whether the label wrapped. Measure it rather than hardcode 63px. */
    function syncHeight() {
      var h = Math.round(bar.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--vv-sticky-atc-h', h + 'px');
    }

    function setShown(shown) {
      bar.classList.toggle('vv-sticky-atc--visible', shown);
      /* Drives the rewards-launcher lift in vv-restyle.css. On <body> rather
         than the bar because the launcher is not a descendant of it — the app
         renders into its own #honeypop-app container. */
      document.body.classList.toggle('vv-sticky-atc-open', shown);
      if (shown) syncHeight();
    }

    /* IntersectionObserver rather than a scroll handler: no listener firing on
       every frame, and it is correct when the page loads already scrolled. */
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        setShown(!entries[0].isIntersecting);
      }, {rootMargin: '0px 0px -40px 0px'}).observe(realBtn);
    } else {
      setShown(true);
    }

    window.addEventListener('resize', syncHeight);

    /* #116 — get out of the way of an open drawer.

       The bar sits at z-index 2147483001 (to beat <shopify-chat>) and the cart
       drawer at 9999, so it covered the drawer's lower ~63px: the checkout
       button and the gift-with-purchase row.

       The theme marks an open drawer with a `js-drawer-open-*` class on <body>,
       which the CSS matches directly. This observer exists only to re-measure
       and to keep the launcher lift in step — the launcher must drop back while
       the bar is hidden, or it floats 63px off the bottom for no reason. */
    new MutationObserver(function () {
      var drawerOpen = /\bjs-drawer-open/.test(document.body.className);
      document.body.classList.toggle(
        'vv-sticky-atc-open',
        !drawerOpen && bar.classList.contains('vv-sticky-atc--visible')
      );
    }).observe(document.body, {attributes: true, attributeFilter: ['class']});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyAtc);
  } else {
    initStickyAtc();
  }

  function initAnnouncementRotator(bar) {
    var query = window.matchMedia('(max-width: 749px)');
    var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    var timer = null;
    var index = 0;

    function messages() {
      /* Children with no visible content are skipped: the free-shipping block
         carries .is-hidden until the cart qualifies, and rotating onto a blank
         message would look like the bar had broken. */
      return Array.prototype.filter.call(bar.children, function (el) {
        return el.offsetParent !== null || el.classList.contains('vv-bar-msg--active');
      });
    }

    function show(list, i) {
      list.forEach(function (el, n) {
        el.classList.toggle('vv-bar-msg--active', n === i);
      });
    }

    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
      bar.classList.remove('top-bar__inline--rotating');
      Array.prototype.forEach.call(bar.children, function (el) {
        el.classList.remove('vv-bar-msg--active');
      });
    }

    function start() {
      var list = messages();
      if (list.length < 2) return;      /* one message needs no rotation */

      bar.classList.add('top-bar__inline--rotating');
      index = 0;
      show(list, index);

      var speed = parseInt(bar.getAttribute('data-vv-rotate-speed'), 10);
      if (!speed || speed < 1000) speed = 7000;

      timer = setInterval(function () {
        var current = messages();
        if (current.length < 2) return;
        index = (index + 1) % current.length;
        show(current, index);
      }, speed);
    }

    function sync() {
      stop();
      if (query.matches && !motion.matches) start();
    }

    sync();
    /* addEventListener on MediaQueryList is unsupported in older Safari, which
       only has addListener. */
    if (query.addEventListener) {
      query.addEventListener('change', sync);
      motion.addEventListener('change', sync);
    } else if (query.addListener) {
      query.addListener(sync);
      motion.addListener(sync);
    }
  }

  function initAnnouncementRotators() {
    document.querySelectorAll('[data-vv-inline][data-vv-rotate-speed]').forEach(initAnnouncementRotator);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAnnouncementRotators);
  } else {
    initAnnouncementRotators();
  }

  document.addEventListener('change', function (event) {
    var select = event.target;
    if (!select.matches || !select.matches('[data-vv-upsell-select]')) return;

    var row = select.closest('.vv-upsell-row');
    if (!row) return;

    var priceEl = row.querySelector('[data-vv-upsell-price]');
    var option = select.options[select.selectedIndex];
    if (!priceEl || !option) return;

    var price = option.getAttribute('data-price');
    if (price) priceEl.textContent = price;
  });

  /* #78: product rails outgrow the height Flickity reserved for them.

     Flickity measures its cells once, at init, and writes an inline height onto
     `.flickity-viewport`. The Atome and ShopBack widgets inject their rows into every
     card AFTER that — roughly 53px per card — so the cells end up taller than the
     viewport and spill out of the section. Measured on staging: viewport 489px, cells
     555px, 66px of overflow landing on top of the next section's heading.

     Flickity cannot be asked to re-measure: there is no reachable instance, and its
     resize handler early-returns when the element's width is unchanged (#74 covers
     the full list of what does not work). What IS reachable is the inline height it
     wrote, so this corrects that value directly.

     A ResizeObserver rather than a timeout because the widgets are third-party and
     render whenever they render — a delay that works on a fast connection is exactly
     the kind of guess that breaks in the field. */
  function initRailHeight(viewport) {
    /* `.product-grid-item` ONLY. An earlier `[class*="grid-item"]` also matched the BEM
       children — `__image`, `__info`, `__title` — so the min-height below was written
       onto elements INSIDE each card, inflating the card that was then measured. That
       fed back through the ResizeObserver and reached 16,777,216px. */
    function cells() {
      return Array.prototype.slice.call(viewport.querySelectorAll('.product-grid-item'));
    }

    /* `applying` guards against the obvious trap, which bit once: the ResizeObserver
       fires on the very writes made here, so re-measuring inside its own callback reads
       the inflated heights back and grows them again. That doubled the cells on every
       pass and reached 16,777,216px before it was caught. */
    var applying = false;

    function fit() {
      if (applying) return;
      var list = cells();
      if (!list.length) return;

      applying = true;

      /* Clear first, then measure. The cells carry a min-height from the previous pass,
         so measuring without clearing reads back what was written rather than what the
         content actually needs. */
      list.forEach(function (cell) { cell.style.minHeight = ''; });

      var tallest = list.reduce(function (max, cell) {
        return Math.max(max, cell.getBoundingClientRect().height);
      }, 0);

      if (tallest) {
        var h = Math.ceil(tallest);
        /* The viewport only grows: Flickity's own value is right for the images, and
           shrinking to a momentarily-collapsed cell is how a gallery ends up 0px (#74). */
        if (h > (parseFloat(viewport.style.height) || 0) + 1) viewport.style.height = h + 'px';

        /* Every cell matched to the tallest, so the CTA lands on the same line across
           the rail — a single-variant product has no swatch row, which left its button
           23px higher than its neighbours'. CSS cannot do this: Flickity's slider is
           absolutely positioned with an AUTO specified height, so `height: 100%` on a
           cell does not resolve. Verified by injecting it with !important and watching
           nothing move. */
        list.forEach(function (cell) { cell.style.minHeight = h + 'px'; });
      }

      requestAnimationFrame(function () { applying = false; });
    }

    fit();

    if ('ResizeObserver' in window) {
      var observer = new ResizeObserver(fit);
      cells().forEach(function (cell) { observer.observe(cell); });
    }

    window.addEventListener('theme:resize:width', fit);
  }

  function initRailHeights() {
    document.querySelectorAll('.flickity-viewport').forEach(function (viewport) {
      if (viewport.querySelector('.product-grid-item')) initRailHeight(viewport);
    });
  }

  /* Runs late: Flickity has to have initialised and written its height before there is
     anything to correct. */
  if (document.readyState === 'complete') {
    initRailHeights();
  } else {
    window.addEventListener('load', initRailHeights);
  }
})();


/* ──────────────────────────────────────────────────────────────────────────
   Cart count badge — keep it in sync after AJAX cart changes.

   sections/header.liquid renders <span class="vv-cart-count" data-vv-cart-count>
   server-side and its comment promised "cart JS can update it". That JS was
   never written, so the badge was correct on page load and frozen afterwards:
   add an item and it still read 0; remove one and it still read the old number.
   Only a refresh fixed it. Reported 2026-10-05.

   Palo Alto itself only ships a dot indicator, so it has nothing that updates a
   numeric badge. It DOES toggle .cart__toggle--has-items (which drives our
   visibility), which is why the stale badge became visible showing "0" rather
   than staying hidden — the symptom in the bug report.

   The theme's cart events carry NO detail payload (verified: detail is null on
   theme:cart:added), and the drawer markup has no count node to read, so the
   count has to come from /cart.js. That is one small request per cart change,
   and it is authoritative for add, remove and quantity change alike — which
   matters because the remove path was broken too.

   The events fire more than once per change (two header cart toggles, desktop
   and mobile), so the fetch is debounced to avoid duplicate requests.

   The class is set from the same fetched count rather than trusted from the
   theme: both derive from the same truth, so they cannot disagree, and the
   badge stays correct even if the theme misses a case.
   ────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var EVENTS = ['theme:cart:added', 'theme:cart:loaded', 'theme:cart:update', 'theme:cart:change'];
  var timer = null;
  var inFlight = false;

  function paint(count) {
    document.querySelectorAll('[data-vv-cart-count]').forEach(function (el) {
      el.textContent = count;
      var toggle = el.closest('.cart__toggle');
      if (toggle) toggle.classList.toggle('cart__toggle--has-items', count > 0);
    });
  }

  function refresh() {
    if (inFlight) return;
    inFlight = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart && typeof cart.item_count === 'number') paint(cart.item_count);
      })
      .catch(function () { /* leave the server-rendered value alone on failure */ })
      .finally(function () { inFlight = false; });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 120);
  }

  EVENTS.forEach(function (name) { document.addEventListener(name, schedule); });
})();

/* ==========================================================================
   #109 — Shade swatch colours from ADMIN, not assets/swatches.json
   --------------------------------------------------------------------------
   Palo Alto paints a swatch dot by looking the option value up BY NAME in
   assets/swatches.json (matchColors() in theme.js, lowercased and whitespace
   stripped) and falling back to #E5E5E5. That file is hand-maintained in the
   theme, so every new shade launched grey until someone edited code and
   deployed. It happened at least twice: Buff/Blush/Poppy/Hush on the Glide-On
   Lipstick and Chai/Runway on the Lip Liner.

   Shopify's own option-value swatches are editable in Admin, are store-level,
   and travel with the product. The theme now reads them: Liquid emits
   data-native-swatch where Admin has a colour, and this applies it.

   ⚠️ WHY JAVASCRIPT, and why it runs LAST.
   The inline `--swatch` style is NOT rendered by Liquid — the server HTML
   contains no --swatch on swatch labels at all. theme.js sets it at runtime.
   So emitting the style from Liquid would simply be overwritten. We let
   matchColors() do its pass, then override only the dots Admin has an opinion
   about.

   ⚠️ ADMIN FIRST, swatches.json AS FALLBACK — never a replacement.
   Measured across 73 colour option values at the time of writing:
       33  Admin == swatches.json   (identical, ZERO mismatches)
       32  swatches.json only       (Admin not set)
        2  Admin only               (Beige/Green — grey before this change)
        6  neither                  (the bug)
   Switching to Admin alone would therefore have turned 32 working swatches
   grey. Dots with no data-native-swatch are left exactly as theme.js painted
   them.

   Zero mismatches is what makes this safe: where both sources exist they agree,
   so no shade that looks right today can change. Verified by a control revert:
   74 swatches before and after, exactly TWO changed (Beige and Green, grey ->
   their Admin colours), and zero previously-coloured swatches moved.

   ⚠️ COLLECTION FILTERS ARE DELIBERATELY NOT COVERED. The colour filter on this
   store is filter.p.tag -- a TAG filter, not an option filter -- and product
   tags carry no swatch object, so filter_value.swatch.color can never resolve.
   A patch there was written, measured as dead code, and removed rather than
   shipped. Filter dots keep using swatches.json. The same applies to the
   tag-grouped swatches in sections/collection.liquid.

   Render sites covered: snippets/product.liquid (two, ~1223 and ~2935) and
   snippets/product-grid-item.liquid (~488). The --onboarding snippets use
   placeholder data and are not real surfaces.
   ========================================================================== */
(function () {
  var ATTR = 'data-native-swatch';

  function apply(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[' + ATTR + ']').forEach(function (el) {
      var color = el.getAttribute(ATTR);
      if (!color) return;
      // Only ever ADD colour. If Admin has no value the attribute is not
      // emitted at all, so swatches.json keeps its say.
      if (el.style.getPropertyValue('--swatch') === color) return;
      el.style.setProperty('--swatch', color);
    });
  }

  function run() { apply(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // theme.js repaints on its own schedule, and sections re-render on variant
  // change, filtering and quick view. Rather than guess its timing, watch for
  // swatch labels entering the DOM and re-apply to those subtrees only.
  if (window.MutationObserver) {
    var pending = null;
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes && records[i].addedNodes.length) {
          clearTimeout(pending);
          pending = setTimeout(run, 50);
          return;
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Shopify section rendering and the theme's own events.
  ['shopify:section:load', 'theme:variant:change', 'theme:filters:update']
    .forEach(function (name) { document.addEventListener(name, function () { run(); }); });
})();

/* ==========================================================================
   FIND US IRL — drag-to-scroll for pointer devices
   --------------------------------------------------------------------------
   The rail is a native overflow container, so TOUCH and WHEEL already scroll it
   and keyboard works via tabindex. A MOUSE cannot: dragging a native scroller
   does nothing, and the scrollbar is hidden by design (the peeking card is the
   affordance). That left mouse users on a narrow window with no way to move it.

   ⚠️ Touch is deliberately EXCLUDED. Native touch scrolling is smoother than
   anything reimplemented here and already handles momentum, rubber-banding and
   scroll-snap. Hijacking it would make the common case worse to fix the rare
   one, so pointerType 'touch' returns immediately.

   Snap is suspended during a drag and restored after: with
   `scroll-snap-type: x mandatory` still applied, every mousemove fights the
   snap and the rail judders instead of following the cursor.
   ========================================================================== */
(function () {
  function initFindUsDrag(rail) {
    if (rail.dataset.vvDragReady) return;
    rail.dataset.vvDragReady = '1';

    var down = false, startX = 0, startScroll = 0, moved = 0;

    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;      /* let the platform do it */
      if (e.button !== 0) return;
      down = true; moved = 0;
      startX = e.clientX;
      startScroll = rail.scrollLeft;
      rail.style.scrollSnapType = 'none';
      rail.style.cursor = 'grabbing';
    });

    rail.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 3) {
        /* Only capture once it is clearly a drag, so a plain click on the
           DIRECTIONS link still works. */
        if (!moved) { try { rail.setPointerCapture(e.pointerId); } catch (err) {} }
        moved = Math.max(moved, Math.abs(dx));
        e.preventDefault();
      }
      rail.scrollLeft = startScroll - dx;
    });

    function end(e) {
      if (!down) return;
      down = false;
      rail.style.cursor = '';
      /* Restore snap on the next frame: setting it synchronously makes the rail
         jump to the nearest card mid-gesture rather than settling from where
         the drag ended. */
      requestAnimationFrame(function () { rail.style.scrollSnapType = ''; });
      if (e && e.pointerId != null) { try { rail.releasePointerCapture(e.pointerId); } catch (err) {} }
    }

    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('pointerleave', end);

    /* A drag that ends on top of the link would otherwise fire a click and
       navigate away from the page. */
    rail.addEventListener('click', function (e) {
      if (moved > 5) { e.preventDefault(); e.stopPropagation(); moved = 0; }
    }, true);
  }

  function initAll() {
    document.querySelectorAll('.vv-find-us__rail').forEach(initFindUsDrag);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();

/* ===========================================================================
   COMMUNITY VIDEOS — "See It On Real People"  (#127, phase 1)
   ---------------------------------------------------------------------------
   Rail paging + the modal player.

   The video markup lives in a <template> per card, so NOTHING is fetched until
   a card is opened. Opening clones the template into a single per-section
   <dialog>; closing empties it, which stops playback and releases the buffer.
   Only one video can exist in the DOM at a time.

   <dialog>.showModal() provides focus trapping, Esc-to-close, an inert
   background and ::backdrop. Hand-rolling those is where modal accessibility
   usually goes wrong, so we lean on the platform.

   ⚠️ SOUND. We attempt to play UNMUTED. Browsers may reject that promise even
   though the open was a user gesture — iOS Safari especially. On rejection we
   mute, retry, and leave the unmute control showing its muted state. These are
   people talking, so muted-by-default would lose the message; this gets sound
   wherever the browser allows it and degrades quietly where it does not.

   ⚠️ LOADING. `preload="none"` means play() is what starts the download, so on
   a slow connection the first seconds show a motionless poster. Reported as
   "it looks stuck". The dialog therefore carries a state attribute
   (data-vv-ugc-state) that drives a spinner, and the state is read from MEDIA
   EVENTS — `playing` / `waiting` — not from whether play() was called. play()
   resolving only means the request was accepted, not that a frame is on screen.

   ⚠️ SAFARI AUTOPLAY. Reported as "did not autostart on Safari".
   CONFIRMED FIXED on a real iPhone, 2026-10-26, against the staging theme.

   But WHICH of the three below fixed it is still not known: headless WebKit
   never reproduced the failure — it autoplays unmuted on localhost with or
   without this change, verified by a control run — so there was no way to
   bisect them. Do not delete any of the three on the grounds that it looks
   redundant. The fix works; the diagnosis was never made.

     1. `preload="none"` + a <video> cloned out of an inert <template>: no
        resource selection has run, so play() can land on an element with no
        selected source. An explicit load() inside the gesture selects one.
     2. The muted retry. Safari weighs the `muted` ATTRIBUTE, not only the
        property, and the retry runs from a promise rejection — outside the
        gesture — where muted is the only thing that makes it permissible.
        The old code set the property alone. This is the likeliest culprit.
     3. Low Power Mode refuses programmatic play() outright, muted or not. No
        code fixes that, so the last resort is a "Tap to play" button: a direct
        tap is a fresh gesture every browser honours. Whatever the real cause
        turns out to be, 3 is what guarantees the viewer is never stranded.

   All three are handled in the gesture-critical order inside open(); do not
   reorder it without re-testing on a real Safari, on a device.
   =========================================================================== */
(function () {

  function initCommunity(root) {
    var section = root.matches && root.matches('.vv-ugc') ? root : root.querySelector('.vv-ugc');
    if (!section || section.dataset.vvUgcReady) return;
    section.dataset.vvUgcReady = '1';

    var rail = section.querySelector('[data-vv-ugc-rail]');
    var dialog = section.querySelector('[data-vv-ugc-dialog]');
    var stage = section.querySelector('[data-vv-ugc-stage]');
    var dotsWrap = section.querySelector('[data-vv-ugc-dots]');
    var prev = section.querySelector('[data-vv-ugc-prev]');
    var next = section.querySelector('[data-vv-ugc-next]');
    var muteBtn = section.querySelector('[data-vv-ugc-mute]');
    var ppBtn = section.querySelector('[data-vv-ugc-playpause]');
    var statusText = section.querySelector('[data-vv-ugc-status-text]');
    var tapBtn = section.querySelector('[data-vv-ugc-tap]');
    if (!rail || !dialog || !stage) return;

    var opener = null;   /* restore focus here on close */
    var video = null;

    /* ---- paging ---------------------------------------------------------- */
    function pageWidth() { return rail.clientWidth; }
    function pageCount() { return Math.max(1, Math.ceil(rail.scrollWidth / pageWidth() - 0.02)); }

    function buildDots() {
      if (!dotsWrap) return;
      var n = pageCount();
      dotsWrap.innerHTML = '';
      if (n <= 1) {
        /* Everything fits: no dots — but the arrows still need their disabled
           state, so DO NOT return before syncUi. Returning early here left the
           prev arrow looking active on a rail that cannot scroll. */
        syncUi();
        return;
      }
      for (var i = 0; i < n; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'vv-ugc__dot';
        b.setAttribute('aria-label', 'Go to page ' + (i + 1));
        b.dataset.page = String(i);
        dotsWrap.appendChild(b);
      }
      syncUi();
    }

    function syncUi() {
      var w = pageWidth();
      var page = Math.round(rail.scrollLeft / w);
      if (dotsWrap) {
        Array.prototype.forEach.call(dotsWrap.children, function (d, i) {
          d.classList.toggle('is-active', i === page);
        });
      }
      var atStart = rail.scrollLeft <= 2;
      var atEnd = rail.scrollLeft + w >= rail.scrollWidth - 2;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
    }

    function scrollByPage(dir) {
      rail.scrollBy({ left: dir * pageWidth(), behavior: 'smooth' });
    }

    if (prev) prev.addEventListener('click', function () { scrollByPage(-1); });
    if (next) next.addEventListener('click', function () { scrollByPage(1); });
    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (e) {
        var d = e.target.closest('.vv-ugc__dot');
        if (!d) return;
        rail.scrollTo({ left: Number(d.dataset.page) * pageWidth(), behavior: 'smooth' });
      });
    }
    rail.addEventListener('scroll', syncUi, { passive: true });
    window.addEventListener('resize', function () { buildDots(); });

    /* Arrows are aria-hidden: they duplicate the rail's own scrolling, which is
       already keyboard-reachable. Enable them for pointer users only. */
    if (prev) prev.removeAttribute('tabindex');
    if (next) next.removeAttribute('tabindex');
    var nav = section.querySelector('.vv-ugc__nav');
    if (nav) nav.removeAttribute('aria-hidden');

    buildDots();

    /* ---- the player ------------------------------------------------------ */
    /* Attribute toggles only. Rewriting innerHTML here detached the clicked
       <path> mid-event and the outside-click guard then closed the modal —
       see the note in the section markup. */
    function setMuteUi(muted) {
      if (!muteBtn) return;
      muteBtn.dataset.muted = muted ? 'true' : 'false';
      muteBtn.setAttribute('aria-pressed', muted ? 'true' : 'false');
      muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
    }
    function setPlayUi(playing) {
      if (!ppBtn) return;
      ppBtn.dataset.playing = playing ? 'true' : 'false';
      ppBtn.setAttribute('aria-label', playing ? 'Pause' : 'Play');
    }

    /* The player has one state at a time and it lives on the dialog, where CSS
       can reach the spinner, the tap button and the controls from a single
       attribute. Driven by media events, never by "we called play()". */
    var STATUS_TEXT = {
      loading: 'Loading video\u2026',
      blocked: '',                                   /* the button speaks for itself */
      /* `stalled` deliberately does NOT claim failure, and keeps the spinner
         running. Nothing decodable has arrived for a while, which on a slow
         connection is true and still working — so it offers a way out without
         telling the viewer something broke. */
      stalled: 'Still loading \u2014 this is slower than usual.',
      error: 'This video could not be loaded.'
    };
    function setState(state) {
      dialog.dataset.vvUgcState = state;
      if (statusText) statusText.textContent = STATUS_TEXT[state] || '';
    }

    /* ---- stall watchdog --------------------------------------------------
       The error path below only catches a clip the browser gives up on. A
       browser that keeps fetching but never decodes a frame reports
       networkState LOADING forever and fires no `error` at all, so the spinner
       would spin for good — the same "it looks stuck" complaint this change
       exists to answer, one layer down.

       Found on the staging preview: a browser sat at networkState LOADING,
       readyState 0, video.error null, for 12+ seconds with no event ever
       fired. ⚠️ The cause was NOT established. It was first put down to a
       missing H.264 decoder, which was wrong — the same browser played the
       same clip minutes later — and the probe that "confirmed" it was run on
       the already-wedged page, so it measured the wedge. Most likely a cold
       CDN fetch wedging the media pipeline. The watchdog does not depend on
       knowing: it bounds any silent non-decoding load, whatever caused it.

       ⚠️ The clock is reset by DECODED progress — readyState and buffered —
       not by the `progress` event. Bytes arriving is exactly what that broken
       case does, so a byte-counting watchdog would never fire on the one
       situation it is for. */
    var STALL_MS = 20000;
    var stallTimer = null;
    var stallSince = 0;
    var stallMark = -1;

    function decodedProgress() {
      if (!video) return -1;
      var end = video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0;
      return video.readyState * 1000 + end;
    }
    function stopStallWatch() {
      if (stallTimer) { clearInterval(stallTimer); stallTimer = null; }
    }
    function startStallWatch() {
      stopStallWatch();
      stallSince = Date.now();
      stallMark = decodedProgress();
      stallTimer = setInterval(function () {
        if (!video) { stopStallWatch(); return; }
        var state = dialog.dataset.vvUgcState;
        if (state !== 'loading') { stopStallWatch(); return; }

        var now = decodedProgress();
        if (now !== stallMark) {        /* something decodable moved — not stalled */
          stallMark = now;
          stallSince = Date.now();
          return;
        }
        if (Date.now() - stallSince < STALL_MS) return;
        stopStallWatch();
        setState('stalled');
      }, 2000);
    }

    /* Mute both ways. Safari reads the ATTRIBUTE when deciding whether a
       programmatic play() is allowed; the property alone leaves it blocked. */
    function forceMute(v) {
      v.muted = true;
      v.setAttribute('muted', '');
      setMuteUi(true);
    }

    /* ⚠️ WHY the rejection reason is inspected rather than treated as "no
       sound allowed". A play() still pending when load() restarts resource
       selection rejects with AbortError, which is not a policy refusal at all
       — and the retry button calls load() immediately before play(). Treating
       that as a refusal muted every recovered video, losing exactly what this
       section is for: people talking. Caught on the stall-recovery path, where
       a retry came back playing but silent. */
    var abortRetried = false;

    function attempt(unmuted) {
      if (!video) return;
      if (unmuted) { video.muted = false; setMuteUi(false); }
      else { forceMute(video); }

      var p;
      try { p = video.play(); } catch (e) { p = null; }

      if (!p || typeof p.catch !== 'function') return;   /* pre-promise browser */
      p.catch(function (err) {
        var name = err && err.name;

        if (name === 'AbortError') {
          /* Our own load() killed it. Try the SAME sound setting again on the
             next tick, once, rather than quietly dropping to muted. */
          if (abortRetried) return;
          abortRetried = true;
          setTimeout(function () { attempt(unmuted); }, 0);
          return;
        }

        if (name === 'NotSupportedError') {
          /* No candidate the browser can play. Not a sound problem — muting
             and retrying would fail identically and look like a hang. */
          stopStallWatch();
          setPlayUi(false);
          setState('error');
          return;
        }

        if (unmuted) {
          /* Sound refused. Mute, retry, and show it as muted so the viewer
             knows one tap gets audio back. */
          attempt(false);
        } else {
          /* Muted was refused too — Low Power Mode, or a policy we cannot talk
             our way past. Hand it to the viewer rather than sit on a poster. */
          stopStallWatch();
          setPlayUi(false);
          setState('blocked');
        }
      });
    }

    function open(card) {
      var tpl = card.querySelector('[data-vv-ugc-payload]');
      if (!tpl) return;
      opener = card.querySelector('[data-vv-ugc-open]');

      stage.innerHTML = '';
      stage.appendChild(tpl.content.cloneNode(true));
      video = stage.querySelector('video');

      /* Show the spinner BEFORE showModal, so the dialog never paints a frame
         in its finished state on the way in. */
      setState('loading');
      setPlayUi(true);

      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');

      if (!video) { setState('error'); return; }

      /* Listeners first. Bound after play() they miss the opening `playing`
         event on a fast connection and the spinner never clears. */
      video.addEventListener('playing', function () {
        stopStallWatch();
        setState('playing');
        setPlayUi(true);
      });
      video.addEventListener('waiting', function () {
        /* Mid-stream stall. Only a spinner if we are actually trying to play —
           `waiting` also fires while seeking a paused video. */
        if (video.paused) return;
        setState('loading');
        startStallWatch();           /* a mid-stream stall can hang just as hard */
      });
      video.addEventListener('play', function () { setPlayUi(true); });
      video.addEventListener('pause', function () {
        setPlayUi(false);
        /* Paused is not loading. Clearing this is what stops a spinner being
           left on screen when the viewer pauses during the initial buffer. */
        if (dialog.dataset.vvUgcState === 'loading') { stopStallWatch(); setState('playing'); }
      });
      video.addEventListener('ended', function () { setPlayUi(false); });

      /* ⚠️ CAPTURE phase, and a networkState check rather than a plain handler.
         When a <source> fails, the error event fires on the SOURCE and does not
         bubble, and with no candidate ever selected the <video> itself never
         fires `error` at all — video.error stays null. A listener bound the
         normal way therefore misses a completely dead clip and the spinner
         spins forever, which is the exact thing this change exists to prevent.
         Capture reaches the descendant's non-bubbling event on the way down.

         The deferred re-check is because the failing <source> fires before the
         browser has decided whether a later candidate works: networkState is
         only NETWORK_NO_SOURCE (3) once they are all exhausted. Erroring
         immediately would blame the first source for a video that plays fine
         from the second. */
      function failIfDead() {
        if (!video) return;
        if (video.error || (video.networkState === 3 && video.readyState === 0)) {
          stopStallWatch();
          setState('error');
          setPlayUi(false);
        }
      }
      video.addEventListener('error', function () { setTimeout(failIfDead, 250); }, true);

      /* Safari has not run resource selection on a <video> cloned out of an
         inert <template> with preload="none", so play() would land on an
         element with no source. load() inside the gesture selects one.
         preload is raised on the CLONE only — the template stays inert. */
      video.preload = 'auto';
      try { video.load(); } catch (e) {}

      abortRetried = false;
      attempt(true);
      startStallWatch();
    }

    function close() {
      if (video) { try { video.pause(); } catch (e) {} }
      /* Emptying the stage removes the <video> entirely, which stops the
         download and frees the buffer. Leaving it paused would keep buffering. */
      stage.innerHTML = '';
      video = null;
      stopStallWatch();
      setState('idle');
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      if (opener) { try { opener.focus(); } catch (e) {} }
      opener = null;
    }

    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-vv-ugc-open]');
      if (!btn) return;
      var card = btn.closest('.vv-ugc__card');
      if (card) open(card);
    });

    var closeBtn = section.querySelector('[data-vv-ugc-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);

    /* Esc fires <dialog>'s cancel — make sure our teardown runs too */
    dialog.addEventListener('cancel', function (e) { e.preventDefault(); close(); });
    dialog.addEventListener('close', function () { if (video) close(); });

    /* Click outside the stage closes, matching the backdrop affordance */
    /* Outside-click closes. Uses composedPath(), captured at dispatch, rather
       than e.target — a target can be detached by the time this runs, which is
       exactly how the mute button used to close the modal. */
    dialog.addEventListener('click', function (e) {
      var path = (typeof e.composedPath === 'function') ? e.composedPath() : [e.target];
      for (var i = 0; i < path.length; i++) {
        var n = path[i];
        if (n === stage || n === closeBtn) return;
        if (n && n.classList && (n.classList.contains('vv-ugc__controls') ||
                                 n.classList.contains('vv-ugc__stage') ||
                                 n.classList.contains('vv-ugc__status'))) return;
      }
      close();
    });

    if (muteBtn) {
      muteBtn.addEventListener('click', function () {
        if (!video) return;
        video.muted = !video.muted;
        setMuteUi(video.muted);
      });
    }
    if (ppBtn) {
      ppBtn.addEventListener('click', function () {
        if (!video) return;
        if (video.paused) video.play(); else video.pause();
      });
    }

    /* One button, two jobs, decided by the state it was shown in:
         blocked          -> "Tap to play". A direct tap is a fresh user
                             gesture, which every browser honours, so try
                             UNMUTED again even though the open was refused.
         stalled / error  -> "Try again". Re-run resource selection from
                             scratch; a half-dead fetch will not recover
                             without it. */
    if (tapBtn) {
      tapBtn.addEventListener('click', function () {
        if (!video) return;
        if (dialog.dataset.vvUgcState !== 'blocked') {
          try { video.load(); } catch (e) {}
        }
        setState('loading');
        abortRetried = false;
        attempt(true);
        startStallWatch();
      });
    }
  }

  function initAllCommunity() {
    document.querySelectorAll('.vv-ugc').forEach(initCommunity);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCommunity);
  } else {
    initAllCommunity();
  }
  document.addEventListener('shopify:section:load', function (e) {
    initCommunity(e.target || document);
  });
})();

/* ===========================================================================
   ROUTINE SELECTOR — "Complete your routine"  (#132)
   ---------------------------------------------------------------------------
   ONE source of truth: `active`, the 1-based step index. The image, the
   selector styling and the detail block are all derived from it. #132 is
   explicit that these must never hold separate state, because that is how the
   image ends up showing a different step from the caption.

   The images are already in the DOM, stacked. Changing step only moves an
   `is-active` class, so the crossfade is pure CSS and cannot blank the frame.

   Text is swapped SEQUENTIALLY — fade out, replace, fade in — not dissolved.
   The reference videos show both strings visible at once; that is a generated
   artefact and unreadable, and the written spec asks for fade-out-then-in.
   =========================================================================== */
(function () {
  function initRoutine(root) {
    var section = root.matches && root.matches('.vv-routine') ? root : root.querySelector('.vv-routine');
    if (!section || section.dataset.vvRoutineReady) return;
    section.dataset.vvRoutineReady = '1';

    var buttons = section.querySelectorAll('[data-vv-routine-step]');
    var images = section.querySelectorAll('[data-vv-routine-img]');
    var detail = section.querySelector('[data-vv-routine-detail]');
    var dataEl = section.querySelector('[data-vv-routine-data]');
    if (!buttons.length || !detail || !dataEl) return;

    var steps;
    try { steps = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!steps || !steps.length) return;

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var active = 1;
    var swapTimer = null;

    function render(n) {
      /* Clicking the current step must not re-run the animation — the spec
         calls that out specifically. */
      if (n === active) return;
      active = n;

      Array.prototype.forEach.call(images, function (img) {
        img.classList.toggle('is-active', Number(img.dataset.vvRoutineImg) === n);
      });

      Array.prototype.forEach.call(buttons, function (b) {
        var on = Number(b.dataset.vvRoutineStep) === n;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });

      var s = steps[n - 1];
      if (!s) return;

      function paint() {
        /* Rebuilt rather than patched, so a step with no caption cannot leave
           the previous step's caption behind. */
        var html = '<p class="vv-routine__index">' + s.indexLabel + ' — ' + s.label + '</p>' +
                   '<p class="vv-routine__title">' + s.title + '</p>';
        if (s.caption) html += '<p class="vv-routine__caption">' + s.caption + '</p>';
        detail.innerHTML = html;
      }

      if (reduced) { paint(); return; }

      clearTimeout(swapTimer);
      detail.classList.add('is-swapping');
      swapTimer = setTimeout(function () {
        paint();
        /* Next frame, so the browser paints the new text at opacity 0 before
           the class is removed — otherwise it appears instantly with no fade. */
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { detail.classList.remove('is-swapping'); });
        });
      }, 220);
    }

    section.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-vv-routine-step]');
      if (!btn) return;
      render(Number(btn.dataset.vvRoutineStep));
    });

    /* Left/right arrows move between steps, which is what a grouped set of
       controls should do. Home/End jump to the ends. */
    section.addEventListener('keydown', function (e) {
      var btn = e.target.closest('[data-vv-routine-step]');
      if (!btn) return;
      var n = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = Math.min(steps.length, active + 1);
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = Math.max(1, active - 1);
      else if (e.key === 'Home') n = 1;
      else if (e.key === 'End') n = steps.length;
      if (n === null) return;
      e.preventDefault();
      render(n);
      var next = section.querySelector('[data-vv-routine-step="' + n + '"]');
      if (next) next.focus();
    });
  }

  function initAllRoutine() {
    document.querySelectorAll('.vv-routine').forEach(initRoutine);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllRoutine);
  } else {
    initAllRoutine();
  }
  document.addEventListener('shopify:section:load', function (e) {
    initRoutine(e.target || document);
  });
})();
